/* eslint-disable react-hooks/exhaustive-deps */
import success from "@assets/images/success.gif";
import { Path } from "@common/constans";
import { ReservationStatus } from "@common/enum";
import Alert from "@components/general/Alert";
import Divider from "@components/general/Divider";
import Button from "@components/general/form/Button";
import Icon from "@components/general/Icon";
import { getFilePath } from "@config/appConfig";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Translate from "@service/Translate";
import { formatToDate } from "@utilize/Format";
import { getPrint } from "@utilize/Window";
import QRCode from "qrcode.react";
import { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import FareRules from "./FareRules";
import FlightDetails from "./FlightDetails";
import PassengersInfo from "./PassengersInfo";

interface ItineraryContentProps {
    printMode?: 'en' | 'fa';
}

const ItineraryContent: FC<ItineraryContentProps> = ({ printMode }) => {
    const { tr } = Translate;
    const { small } = Config.config.viewport;
    const { pnrInfo, width } = useSelector((state: IAppState) => ({ pnrInfo: state.pnrInfo, width: state.windowWidth }));
    const visibleFlight = pnrInfo?.originDestinations?.filter(o => o.visibility && o.status !== ReservationStatus.Cancel) || [];

    useEffect(() => {
        const isIE11 = navigator.userAgent.indexOf("Trident/") > -1;
        window.addEventListener("keydown", handlePrintEvent, isIE11 ? { capture: true } : true);
        return (() => {
            window.removeEventListener("keydown", handlePrintEvent, isIE11 ? { capture: true } : true);
        })
    }, [])

    const handlePrintEvent = (e: KeyboardEvent) => {
        if (e.ctrlKey && e.code === 'KeyP') {
            e.preventDefault();
        }
    }

    return (
        <div className="container">
            <div className={`itinerary-header flex justify-between flex-center flex-wrap${printMode ? ` ${printMode}-mode` : ''}`}>
                <div className="flex flex-center itinerary-success">
                    <img src={success} alt="success" className="icon-success" />
                    <div className="desc">{tr("itinerary_msg_successfull")}</div>
                </div>
                <div className="flex flex-center flex-wrap justify-between itinerary-print">
                    {
                        printMode === 'en' ?
                            <>
                                <Alert
                                    variant="none"
                                    type="information"
                                    className="itinerary-print-alert"
                                    icon="icon-information-outline"
                                    seperate
                                >{tr('itinerary_print_warning')}</Alert>
                                <Button
                                    className="itinerary-print-btn"
                                    variant="contained"
                                    color="primary"
                                    text={tr("print_ticket")}
                                    url={`/en${Path.Booking.ItineraryEn}?${pnrInfo.transactionIdentifier ? `transactionId=${pnrInfo.transactionIdentifier}` : `pnr=${pnrInfo.pnr}&name=${pnrInfo.contactPerson?.lastName}&date=${pnrInfo.originDestinations?.length ? pnrInfo.originDestinations[0].departureDateTime : 0}`}`}
                                    target="_blank"
                                />
                            </>
                            :
                            width <= small ? <Icon name="icon-print" onClick={() => getPrint()} /> :
                                <Button
                                    className="itinerary-print-btn"
                                    variant="contained"
                                    color="primary"
                                    text={tr("print_ticket")}
                                    onClick={() => getPrint()}
                                    height="40px"
                                />
                    }
                </div>
            </div>
            <div className="itinerary-content">
                <div className="itinerary-content-header">
                    <Icon name="icon-mahan-air-logo" />
                    <div className="pnr-info-itinerary">
                        <div className="divider-box">
                            <div className="divider-box-section">
                                <span className='divider-box-section-item'>
                                    <span className="divider-box-section-title">{tr("pnr")}: </span>
                                    <span>{pnrInfo?.pnr}</span>
                                </span>
                                <div className="divider-line"></div>
                            </div>
                            <div className="divider-box-section">
                                <span className='divider-box-section-item'>
                                    <span className="divider-box-section-title">{tr("reservation_date")}: </span>
                                    <span>{formatToDate(pnrInfo?.releaseDate || 0, { format: "d M Y" })}</span>
                                </span>
                            </div>
                        </div>
                        <a href={pnrInfo?.manageUrl} className="qr-code" target="_blank" rel="noopener noreferrer">
                            <QRCode
                                value={pnrInfo?.manageUrl || ""}
                                className="qr"
                                size={85}
                                renderAs="svg"
                                bgColor="#f2f2f2"
                                includeMargin={false}
                            />
                        </a>
                    </div>
                </div>
                {
                    visibleFlight.length ? <FlightDetails flights={visibleFlight} /> : ""
                }
                {
                    pnrInfo.travelers?.length ? <PassengersInfo /> : ""
                }
                <FareRules originDestinations={pnrInfo?.originDestinations || []} />
                <div className="itinerary-content-footer">
                    <div className="itinerary-content-footer-booking-issue flex flex-center row spa-2">
                        <div className={`col-1-${pnrInfo.agent?.stamp ? "4" : "3"} col-lg-1-2 col-md-1-2 col-sm-1-1 itinerary-content-footer-booking-issue-items`}>
                            <div className="itinerary-content-footer-booking-issue-items-content">
                                <div className="title">{tr("itinerary_booking_issue_title")}</div>
                                <div className="desc">{pnrInfo.agent?.name}</div>
                                <Icon name="icon-office-building" />
                            </div>
                        </div>
                        <div className={`col-1-${pnrInfo.agent?.stamp ? "4" : "3"} col-lg-1-2 col-md-1-2 col-sm-1-1 itinerary-content-footer-booking-issue-items`}>
                            <div className="itinerary-content-footer-booking-issue-items-content">
                                <div className="title">{tr("call_support")}</div>
                                <div className="desc">
                                    <a className="ltr" href={`tel:${pnrInfo.agent?.phone}`}>
                                        {pnrInfo.agent?.phone}
                                    </a>
                                </div>
                                <Icon name="icon-phone" />
                            </div>
                        </div>
                        <div className={`col-1-${pnrInfo.agent?.stamp ? "4" : "3"} col-lg-1-2 col-md-1-2 col-sm-1-1 itinerary-content-footer-booking-issue-items support-ibe`}>
                            <div className="itinerary-content-footer-booking-issue-items-content">
                                <div className="title">{tr("email_address")}</div>
                                <div className="desc">
                                    <a className="ltr" href={`mailto:${pnrInfo.agent?.email}`}>
                                        {pnrInfo.agent?.email}
                                    </a>
                                </div>
                                <Icon name="icon-email-outline" />
                            </div>
                        </div>
                        {
                            pnrInfo.agent?.stamp ?
                                <div className={`col-1-${pnrInfo.agent?.stamp ? "4" : "3"} col-lg-1-2 col-md-1-2 col-sm-1-1 flex flex-center justify-end itinerary-content-footer-booking-issue-items-stamp`}>
                                    <img src={getFilePath(pnrInfo.agent.stamp)} alt={"booking-stamp"} />
                                </div>
                                : ""
                        }
                    </div>
                    <Alert
                        type="defult"
                        icon="icon-information-outline"
                        className="itinerary-refund-change-alert"
                    >
                        {tr("itinerary_info")}
                    </Alert>
                    <Divider className="itinerary-content-footer-divider" mode="default" orientation="horizontal" />
                    <div className="itinerary-content-footer-address flex justify-between">
                        <div className="title">{pnrInfo.agent?.address}</div>
                        <div className="address">{window.location.host}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ItineraryContent;